import React from 'react';
import {
  Box,
  Typography,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import Inventory2OutlinedIcon from '@mui/icons-material/Inventory2Outlined';
import { ReportFooterTimestamp, numberToWords } from '../../../utils/reportUtils';
import useCurrency from '../../CurrencyProvider';
import { ClinicHeader } from '../ClinicHeader';
import PatientDetailsForReport from '../PatientDetailsForReport';
import { InvoiceDetailsResponse } from '../../../pages/Billing/Invoice/newInvoice/newInvoice.types';
import { formatDateTimeAmPm } from '../../../utils/dateUtils';

type Props = {
  invoiceData: InvoiceDetailsResponse;
};

const InvoiceReport: React.FC<Props> = ({ invoiceData }) => {
  const { formatCurrency } = useCurrency();

  const {
    bill_number,
    bill_type,
    visit_date,
    services,
    total_amount,
    concession_amount,
    discount_amount,
    tax_amount,
    net_amount,
    paid_amount,
    balance_amount,
    concession_reason,
    due_date,
    terms_and_conditions,
    legal_mentions,
    remark,
    comments,
    status,
    patient,
    invoice_patient_type,
    invoice_payments,
    created_on,
    created_by_name,
  } = invoiceData;

  const invoiceDateTime = created_on ? formatDateTimeAmPm(created_on) : '-';
  const issuer = created_by_name || '-';

  // Flatten payment details from all payments
  const allPaymentDetails = (invoice_payments?.payments ?? []).flatMap((p) => p.details ?? []);

  const cellStyle = { py: 1, px: 1, fontSize: 12, borderBottom: '1px solid #ddd' };
  const headerCellStyle = { ...cellStyle, fontWeight: 600, backgroundColor: '#f5f5f5', color: '#333' };

  return (
    <Box
      sx={{
        p: 2,
        backgroundColor: '#fff',
        color: '#333',
        fontFamily: 'Arial, sans-serif',
        fontSize: 12,
      }}
    >
      {/* Clinic Header */}
      <ClinicHeader />

      {/* Patient Details Header */}
      <PatientDetailsForReport patientId={patient} isDonor={invoice_patient_type === 'donor'} />

      {/* Invoice Title */}
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 600,
          fontSize: 14,
          mt: 1,
          mb: 1,
          py: 0.5,
        }}
      >
        Invoice Details
      </Typography>

      {/* Invoice Details Row */}
      <Box sx={{ mb: 2 }}>
        <Table size="small" sx={{ border: '1px solid #ddd' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600, width: '15%' }}>Invoice No:</TableCell>
              <TableCell sx={{ ...cellStyle, width: '35%' }}>{bill_number || 'N/A'}</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 600, width: '15%' }}>Status:</TableCell>
              <TableCell sx={{ ...cellStyle, width: '35%', textTransform: 'capitalize' }}>{status || 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Visit Date:</TableCell>
              <TableCell sx={cellStyle}>{visit_date || 'N/A'}</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Due Date:</TableCell>
              <TableCell sx={cellStyle}>{due_date || 'N/A'}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Bill Type:</TableCell>
              <TableCell sx={cellStyle}>{bill_type || 'N/A'}</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Invoice Date & Time:</TableCell>
              <TableCell sx={cellStyle}>{invoiceDateTime}</TableCell>
            </TableRow>
            {issuer && issuer !== '-' && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 600, width: '15%' }}>Issuer:</TableCell>
                <TableCell sx={{ ...cellStyle, width: '35%' }}>{issuer}</TableCell>
                <TableCell sx={{ ...cellStyle, width: '15%' }} />
                <TableCell sx={{ ...cellStyle, width: '35%' }} />
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Box>

      {/* Services Table */}
      <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
        Services
      </Typography>
      <TableContainer sx={{ mb: 2, border: '1px solid #ddd' }}>
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell sx={{ ...headerCellStyle, width: 40 }}>#</TableCell>
              <TableCell sx={headerCellStyle}>Date</TableCell>
              <TableCell sx={headerCellStyle}>Code</TableCell>
              <TableCell sx={headerCellStyle}>Service</TableCell>
              <TableCell sx={headerCellStyle}>Provider</TableCell>
              <TableCell sx={headerCellStyle} align="right">Charges</TableCell>
              <TableCell sx={headerCellStyle} align="right">Concession</TableCell>
              <TableCell sx={headerCellStyle} align="right">Tax</TableCell>
              <TableCell sx={headerCellStyle} align="right">Net Amount</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {services.map((service, index) => (
              <TableRow key={index}>
                <TableCell sx={cellStyle}>{index + 1}</TableCell>
                <TableCell sx={cellStyle}>{service.date || 'N/A'}</TableCell>
                <TableCell sx={cellStyle}>{service.billing_source_code || 'N/A'}</TableCell>
                <TableCell sx={cellStyle}>
                  <Box>
                    {service.billing_source_name}
                    {service.package_name && (
                      <Typography
                        component="span"
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5,
                          ml: 1,
                          px: 0.75,
                          py: 0.25,
                          fontSize: 10,
                          fontWeight: 500,
                          color: '#1565c0',
                          backgroundColor: '#e3f2fd',
                          borderRadius: '4px',
                          verticalAlign: 'middle',
                        }}
                      >
                        <Inventory2OutlinedIcon sx={{ fontSize: 12 }} />
                        {service.package_name}
                      </Typography>
                    )}
                  </Box>
                </TableCell>
                <TableCell sx={cellStyle}>{service.provider_name || 'N/A'}</TableCell>
                <TableCell sx={cellStyle} align="right">{formatCurrency(Number(service.charges) || 0)}</TableCell>
                <TableCell sx={cellStyle} align="right">{formatCurrency(Number(service.concession_amount) || 0)}</TableCell>
                <TableCell sx={cellStyle} align="right">{formatCurrency(Number(service.tax_amount) || 0)}</TableCell>
                <TableCell sx={{ ...cellStyle, fontWeight: 500 }} align="right">{formatCurrency(Number(service.net_amount) || 0)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Payment Details */}
      {allPaymentDetails.length > 0 && (
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 1 }}>
            Payment Details
          </Typography>
          <Table size="small" sx={{ border: '1px solid #ddd' }}>
            <TableHead>
              <TableRow>
                <TableCell sx={{ ...headerCellStyle, width: 32 }}>#</TableCell>
                <TableCell sx={headerCellStyle}>Payment Mode</TableCell>
                <TableCell sx={{ ...headerCellStyle, width: 140 }} align="right">Amount</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allPaymentDetails.map((detail, idx) => (
                <TableRow key={idx}>
                  <TableCell sx={{ ...cellStyle, color: '#888' }}>{idx + 1}</TableCell>
                  <TableCell sx={{ ...cellStyle, fontWeight: 500 }}>{detail.payment_mode || 'N/A'}{detail.number ? ` — ${detail.number}` : ''}</TableCell>
                  <TableCell sx={{ ...cellStyle, fontFamily: 'monospace' }} align="right">
                    {formatCurrency(Number(detail.paid_amount) || 0)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Box>
      )}
      
      {/* Summary Table */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2 }}>
        <Table size="small" sx={{ width: '42%', minWidth: 260, border: '1px solid #ddd' }}>
          <TableBody>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Total Amount:</TableCell>
              <TableCell sx={cellStyle} align="right">{formatCurrency(Number(total_amount) || 0)}</TableCell>
            </TableRow>
            {Number(concession_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Concession:</TableCell>
                <TableCell sx={cellStyle} align="right">(-) {formatCurrency(Number(concession_amount) || 0)}</TableCell>
              </TableRow>
            )}
            {Number(discount_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Discount:</TableCell>
                <TableCell sx={cellStyle} align="right">(-) {formatCurrency(Number(discount_amount) || 0)}</TableCell>
              </TableRow>
            )}
            {Number(tax_amount) > 0 && (
              <TableRow>
                <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Tax:</TableCell>
                <TableCell sx={cellStyle} align="right">(+) {formatCurrency(Number(tax_amount) || 0)}</TableCell>
              </TableRow>
            )}
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell sx={{ ...cellStyle, fontWeight: 700 }}>Net Amount:</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: 700 }} align="right">{formatCurrency(Number(net_amount) || 0)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Paid Amount:</TableCell>
              <TableCell sx={cellStyle} align="right">{formatCurrency(Number(paid_amount) || 0)}</TableCell>
            </TableRow>
            <TableRow>
              <TableCell sx={{ ...cellStyle, fontWeight: 600 }}>Balance:</TableCell>
              <TableCell sx={{ ...cellStyle, fontWeight: Number(balance_amount) > 0 ? 600 : 400 }} align="right">
                {formatCurrency(Number(balance_amount) || 0)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </Box>

      {/* Amount in Words */}
      <Box sx={{ mb: 2, px: 1, py: 0.75, border: '1px solid #ddd', borderRadius: 1, backgroundColor: '#fafafa' }}>
        <Typography variant="body2" component="span" sx={{ fontWeight: 600, fontSize: 12 }}>
          Amount in Words:{' '}
        </Typography>
        <Typography variant="body2" component="span" sx={{ fontSize: 12, fontStyle: 'italic' }}>
          {numberToWords(Number(net_amount) || 0)}
        </Typography>
      </Box>


      {/* Additional Information */}
      {concession_reason && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Concession Reason:{' '}
          </Typography>
          <Typography variant="body2" component="span">
            {concession_reason}
          </Typography>
        </Box>
      )}

      {terms_and_conditions && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Terms and Conditions:</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{terms_and_conditions}</Typography>
        </Box>
      )}

      {legal_mentions && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600 }}>Legal Mentions:</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{legal_mentions}</Typography>
        </Box>
      )}

      {remark && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Remark:{' '}
          </Typography>
          <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {remark}
          </Typography>
        </Box>
      )}

      {comments && (
        <Box sx={{ mb: 1.5 }}>
          <Typography variant="body2" component="span" sx={{ fontWeight: 600 }}>
            Comments:{' '}
          </Typography>
          <Typography variant="body2" component="span" sx={{ whiteSpace: 'pre-wrap' }}>
            {comments}
          </Typography>
        </Box>
      )}

      <Box sx={{ borderTop: '1px solid #ddd', mt: 2, pt: 1 }}>
        <ReportFooterTimestamp />
      </Box>
    </Box>
  );
};

export default InvoiceReport;
